import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Navigation from './components/Navigation';
import SidebarMobile from './components/SidebarMobile';
import ListaClientes from './views/ListaClientes';
import ClienteForm from './views/ClienteForm';
import CristalesHerramienta from './views/CristalesHerramienta';
import Dashboard from './views/Dashboard';
import ListaPrecios from './views/ListaPrecios';
import Finanzas from './views/Finanzas';
import Balance from './views/Balance';
import Facturacion from './views/Facturacion';
import CalendarioPagos from './views/CalendarioPagos';
import AnteojosTodos from './views/AnteojosTodos';
import AnteojosTomados from './views/AnteojosTomados';
import AnteojosPreparados from './views/AnteojosPreparados';
import AnteojosLaboratorio from './views/AnteojosLaboratorio';
import AnteojosParaRetirar from './views/AnteojosParaRetirar';
import AnteojosEntregados from './views/AnteojosEntregados';
import AnteojosLayout from './components/AnteojosLayout';
import StockArmazones from './views/StockArmazones';
import PublicacionesEcommerce from './views/PublicacionesEcommerce';
import GestionEnvios from './views/GestionEnvios';
import Mensajeria from './views/Mensajeria';
import Presupuestos from './views/Presupuestos';
import EstadisticasVentas from './views/EstadisticasVentas';
import GestionNotificaciones from './views/GestionNotificaciones';
import FloatingAddButton from './components/FloatingAddButton';
import VentaModal from './components/VentaModal';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import { authService } from './api/auth';
import SuperAdminDashboard from './views/SuperAdminDashboard';
import { puedeAccederVista } from './utils/modulosSistema';
import ModalDemoTour from './components/ModalDemoTour';
import ModalRegistroOpticaDemo from './components/ModalRegistroOpticaDemo';
import { isDemoUser, abrirTourDemo, solicitarRegistroDemo } from './utils/demoMode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_APP_TITLE = 'Sistema Optica';
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/assets/images/H+L.png`;

const NOMBRES_VISTAS = {
  dashboard: 'Dashboard',
  clientes: 'Clientes',
  'lista-precios': 'Lista de Precios',
  'nuevo-cliente': 'Nuevo Cliente',
  finanzas: 'Finanzas',
  balance: 'Balance',
  facturacion: 'Facturación',
  'finanzas-calendario': 'Calendario de Pagos',
  'anteojos-todos': 'Anteojos · Todos',
  'anteojos-tomados': 'Anteojos · Tomados',
  'anteojos-preparados': 'Anteojos · Preparados',
  'anteojos-laboratorio': 'Anteojos · En Laboratorio',
  'anteojos-para-retirar': 'Anteojos · Para Retirar',
  'anteojos-entregados': 'Anteojos · Entregados',
  'agregar-cristales': 'Cristales',
  'stock-armazones': 'Stock Armazones',
  'vendedor-ecommerce': 'Publicaciones Web',
  'gestion-envios': 'Gestión de Envíos',
  mensajeria: 'Mensajería',
  presupuestos: 'Presupuestos',
  'gestion-notificaciones': 'Notificaciones',
  'estadisticas-ventas': 'Estadísticas de Ventas',
  superadmin: 'Super Admin'
};

function obtenerNombreVista(viewKey) {
  return NOMBRES_VISTAS[viewKey] || 'Dashboard';
}

function convertGoogleDriveUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed.includes('drive.google.com')) return trimmed;

  let fileId = null;
  const matchFile = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (matchFile) fileId = matchFile[1];
  else {
    const matchId = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (matchId) fileId = matchId[1];
  }

  if (fileId) return `https://lh3.googleusercontent.com/d/${fileId}`;
  return trimmed;
}

function resolveOpticaAssetUrl(url) {
  if (!url) return null;
  const converted = convertGoogleDriveUrl(url);
  if (converted.startsWith('/')) return `${API_URL}${converted}`;
  return converted;
}

function updateDocumentFavicon(href) {
  let favicon = document.querySelector("link[rel='icon']");
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.setAttribute('rel', 'icon');
    document.head.appendChild(favicon);
  }
  favicon.setAttribute('type', 'image/png');
  favicon.setAttribute('href', href);
}

async function optimizeFaviconForTab(sourceUrl, logoTabScale = 1.12) {
  if (!sourceUrl) return sourceUrl;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const scanCanvas = document.createElement('canvas');
        scanCanvas.width = img.naturalWidth;
        scanCanvas.height = img.naturalHeight;
        const scanCtx = scanCanvas.getContext('2d', { willReadFrequently: true });

        if (!scanCtx) {
          resolve(sourceUrl);
          return;
        }

        scanCtx.drawImage(img, 0, 0);
        const { data, width, height } = scanCtx.getImageData(0, 0, scanCanvas.width, scanCanvas.height);

        let minX = width;
        let minY = height;
        let maxX = -1;
        let maxY = -1;

        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const alpha = data[(y * width + x) * 4 + 3];
            if (alpha > 8) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (maxX < minX || maxY < minY) {
          resolve(sourceUrl);
          return;
        }

        const cropW = maxX - minX + 1;
        const cropH = maxY - minY + 1;

        const outSize = 64;
        const padding = 2;
        const outCanvas = document.createElement('canvas');
        outCanvas.width = outSize;
        outCanvas.height = outSize;
        const outCtx = outCanvas.getContext('2d');

        if (!outCtx) {
          resolve(sourceUrl);
          return;
        }

        const available = outSize - padding * 2;
        // Slight boost makes the mark look less tiny in browser tabs.
        const boost = Math.max(0.8, Math.min(1.8, Number(logoTabScale) || 1.12));
        const baseScale = Math.min(available / cropW, available / cropH);
        const scale = baseScale * boost;
        const drawW = cropW * scale;
        const drawH = cropH * scale;
        const dx = (outSize - drawW) / 2;
        const dy = (outSize - drawH) / 2;

        outCtx.clearRect(0, 0, outSize, outSize);
        outCtx.drawImage(img, minX, minY, cropW, cropH, dx, dy, drawW, drawH);

        resolve(outCanvas.toDataURL('image/png'));
      } catch (_error) {
        resolve(sourceUrl);
      }
    };

    img.onerror = () => resolve(sourceUrl);
    img.src = sourceUrl;
  });
}

function App() {
  const [usuario, setUsuario] = useState(authService.obtenerUsuarioActual());
  const [authView, setAuthView] = useState('login'); // 'login' | 'register'
  const [currentView, setCurrentView] = useState(() => {
    const saved = localStorage.getItem('currentView');
    return saved || 'dashboard';
  });
  const [viewHistory, setViewHistory] = useState([]);
  const [mostrarNuevaVenta, setMostrarNuevaVenta] = useState(false);
  const [mostrarTourDemo, setMostrarTourDemo] = useState(false);
  const [mostrarRegistroTrial, setMostrarRegistroTrial] = useState(false);
  const [motivoRegistroTrial, setMotivoRegistroTrial] = useState('');
  const [modulosHabilitados, setModulosHabilitados] = useState(null);
  const [logoConfig, setLogoConfig] = useState({ src: null, alt: 'Logo óptica', nombre: '' });
  const [logoScale, setLogoScale] = useState(1);
  const esSuperAdmin = usuario?.rol === 'superadmin';
  const esAdminOGerente = usuario?.rol === 'admin' || usuario?.rol === 'gerente' || esSuperAdmin;

  const aplicarBrandingDefault = useCallback(() => {
    document.title = DEFAULT_APP_TITLE;
    updateDocumentFavicon(DEFAULT_FAVICON);
  }, []);

  const cargarBrandingOptica = useCallback(async () => {
    if (!usuario?.optica_id || !authService.estaAutenticado()) {
      aplicarBrandingDefault();
      setModulosHabilitados(null);
      setLogoConfig({ src: null, alt: 'Logo óptica', nombre: '' });
      setLogoScale(1);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/mi-optica`, {
        headers: {
          ...authService.obtenerHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error('No se pudo obtener la óptica actual');
      }

      const data = await response.json();
      setModulosHabilitados(Array.isArray(data.modulos_habilitados) ? data.modulos_habilitados : null);
      document.title = data.nombre?.trim() || DEFAULT_APP_TITLE;

      // Actualizar logo para Navigation
      const logoUrl = data.logo_url;
      const resolvedLogoUrl = resolveOpticaAssetUrl(logoUrl);
      setLogoConfig({
        src: resolvedLogoUrl || null,
        alt: data.nombre || 'Logo óptica',
        nombre: data.nombre || '',
      });
      setLogoScale(Number(data.logo_scale || 1));

      // Actualizar favicon
      const tabLogo = data.logo_tab_url || data.tab_logo_url || data.logo_url;
      const tabLogoUrl = resolveOpticaAssetUrl(tabLogo) || DEFAULT_FAVICON;
      const tabLogoScale = Number(data.logo_tab_scale || 1.12);
      const optimizedFavicon = await optimizeFaviconForTab(tabLogoUrl, tabLogoScale);
      updateDocumentFavicon(optimizedFavicon);
    } catch (_error) {
      setModulosHabilitados(null);
      aplicarBrandingDefault();
      setLogoConfig({ src: null, alt: 'Logo óptica', nombre: '' });
      setLogoScale(1);
    }
  }, [aplicarBrandingDefault, usuario?.optica_id]);

  const handleLogout = () => {
    authService.logout();
    setUsuario(null);
    setModulosHabilitados(null);
    setCurrentView('dashboard');
    setViewHistory([]);
    localStorage.removeItem('currentView');
  };

  const handleLoginSuccess = (usuarioAutenticado) => {
    setUsuario(usuarioAutenticado);
    setCurrentView('dashboard');
    setViewHistory([]);
    if (isDemoUser(usuarioAutenticado)) {
      sessionStorage.removeItem('demo_tour_visto');
      setMostrarTourDemo(true);
    }
  };

  const handleRegistroTrialExitoso = (nuevoUsuario) => {
    setUsuario(nuevoUsuario);
    setCurrentView('dashboard');
    setViewHistory([]);
    sessionStorage.setItem('demo_tour_visto', 'true');
    localStorage.removeItem('currentView');
    cargarBrandingOptica();
  };

  useEffect(() => {
    if (isDemoUser(usuario)) {
      const visto = sessionStorage.getItem('demo_tour_visto');
      if (!visto) {
        setMostrarTourDemo(true);
      }
    }
  }, [usuario]);

  useEffect(() => {
    const handleAbrirTour = () => setMostrarTourDemo(true);
    const handleSolicitarRegistro = (e) => {
      setMotivoRegistroTrial(e?.detail?.motivo || 'cargar información');
      setMostrarRegistroTrial(true);
    };

    window.addEventListener('demo:abrir-tour', handleAbrirTour);
    window.addEventListener('demo:solicitar-registro', handleSolicitarRegistro);
    return () => {
      window.removeEventListener('demo:abrir-tour', handleAbrirTour);
      window.removeEventListener('demo:solicitar-registro', handleSolicitarRegistro);
    };
  }, []);

  const handleViewChange = useCallback((view) => {
    let targetView = view;

    if (targetView === 'superadmin' && !esSuperAdmin) {
      targetView = 'dashboard';
    } else if (!puedeAccederVista(targetView, modulosHabilitados, esSuperAdmin)) {
      targetView = 'dashboard';
    }

    setCurrentView((prevView) => {
      if (prevView && prevView !== targetView) {
        setViewHistory((hist) => [...hist.slice(-19), prevView]);
        try {
          window.history.pushState({ view: targetView }, '');
        } catch (_e) {
          // silencioso
        }
      }
      return targetView;
    });
  }, [esSuperAdmin, modulosHabilitados]);

  const handleGoBack = useCallback(() => {
    if (viewHistory.length > 0) {
      const vistaPrevia = viewHistory[viewHistory.length - 1];
      setViewHistory((hist) => hist.slice(0, -1));
      setCurrentView(vistaPrevia);
      return;
    }

    if (currentView !== 'dashboard') {
      setCurrentView('dashboard');
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
    }
  }, [viewHistory, currentView]);

  // Sincronizar con el botón "Atrás" físico del navegador / celular
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state?.view) {
        setViewHistory((hist) => hist.slice(0, -1));
        setCurrentView(event.state.view);
      } else if (currentView !== 'dashboard') {
        setCurrentView('dashboard');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'superadmin' && !esSuperAdmin) {
      setCurrentView('dashboard');
      return;
    }

    if (!puedeAccederVista(currentView, modulosHabilitados, esSuperAdmin)) {
      setCurrentView('dashboard');
    }
  }, [currentView, esSuperAdmin, modulosHabilitados]);

  useEffect(() => {
    const handleExternalViewChange = (event) => {
      const view = event?.detail?.view;
      if (!view) return;
      handleViewChange(view);
    };

    globalThis.addEventListener('app-view-change', handleExternalViewChange);
    return () => globalThis.removeEventListener('app-view-change', handleExternalViewChange);
  }, [handleViewChange]);

  useEffect(() => {
    const handleSessionExpired = () => {
      setUsuario(null);
      setCurrentView('dashboard');
      setViewHistory([]);
      aplicarBrandingDefault();
      localStorage.removeItem('currentView');
    };

    globalThis.addEventListener('auth:session-expired', handleSessionExpired);
    return () => globalThis.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [aplicarBrandingDefault]);

  useEffect(() => {
    cargarBrandingOptica();
  }, [cargarBrandingOptica, usuario?.id]);

  useEffect(() => {
    const handleLogoUpdated = () => {
      cargarBrandingOptica();
    };

    window.addEventListener('optica-logo-updated', handleLogoUpdated);
    return () => window.removeEventListener('optica-logo-updated', handleLogoUpdated);
  }, [cargarBrandingOptica]);

  // Guardar currentView en localStorage cada vez que cambia
  useEffect(() => {
    localStorage.setItem('currentView', currentView);
  }, [currentView]);

  if (!usuario || !authService.estaAutenticado()) {
    if (authView === 'register') {
      return (
        <RegisterForm
          onGoToLogin={() => setAuthView('login')}
          onRegisterSuccess={() => setAuthView('login')}
        />
      );
    }
    return (
      <LoginForm
        onLoginSuccess={handleLoginSuccess}
        onGoToRegister={() => setAuthView('register')}
      />
    );
  }

  let viewComponent = null;
  switch (currentView) {
    case 'superadmin':
      viewComponent = esSuperAdmin ? <SuperAdminDashboard /> : <Dashboard onViewChange={handleViewChange} usuario={usuario} />;
      break;
    case 'dashboard':
      viewComponent = <Dashboard onViewChange={handleViewChange} usuario={usuario} />;
      break;
    case 'clientes':
      viewComponent = <ListaClientes onViewChange={handleViewChange} />;
      break;
    case 'lista-precios':
      viewComponent = <ListaPrecios />;
      break;
    case 'nuevo-cliente':
      viewComponent = <ClienteForm onViewChange={handleViewChange} />;
      break;
    case 'finanzas':
      viewComponent = <Finanzas onViewChange={handleViewChange} />;
      break;
    case 'balance':
      viewComponent = <Balance />;
      break;
    case 'facturacion':
      viewComponent = <Facturacion />;
      break;
    case 'finanzas-calendario':
      viewComponent = <CalendarioPagos modoPagina onClose={() => handleViewChange('finanzas')} />;
      break;
    case 'anteojos-todos':
    case 'anteojos-tomados':
    case 'anteojos-preparados':
    case 'anteojos-laboratorio':
    case 'anteojos-para-retirar':
    case 'anteojos-entregados':
      viewComponent = <AnteojosLayout currentView={currentView} onViewChange={handleViewChange} />;
      break;
    case 'agregar-cristales':
      viewComponent = <CristalesHerramienta />;
      break;
    case 'stock-armazones':
      viewComponent = <StockArmazones />;
      break;
    case 'vendedor-ecommerce':
      viewComponent = <PublicacionesEcommerce />;
      break;
    case 'gestion-envios':
      viewComponent = <GestionEnvios onViewChange={handleViewChange} usuario={usuario} />;
      break;
    case 'mensajeria':
      viewComponent = <Mensajeria />;
      break;
    case 'presupuestos':
      viewComponent = <Presupuestos />;
      break;
    case 'gestion-notificaciones':
      viewComponent = <GestionNotificaciones onViewChange={handleViewChange} usuario={usuario} />;
      break;
    case 'estadisticas-ventas':
      viewComponent = esAdminOGerente ? <EstadisticasVentas /> : <ListaClientes onViewChange={handleViewChange} />;
      break;
    default:
      viewComponent = <ListaClientes onViewChange={handleViewChange} />;
      break;
  }

  const vistaAnterior = viewHistory.length > 0
    ? viewHistory[viewHistory.length - 1]
    : currentView !== 'dashboard'
      ? 'dashboard'
      : null;

  const modalGlobalAbierto = mostrarTourDemo || mostrarRegistroTrial || mostrarNuevaVenta;

  return (
    <div>
      <SidebarMobile
        currentView={currentView}
        onViewChange={handleViewChange}
        usuario={usuario}
        modulosHabilitados={modulosHabilitados}
        onLogout={handleLogout}
      />
      <Navigation
        currentView={currentView}
        onViewChange={handleViewChange}
        usuario={usuario}
        modulosHabilitados={modulosHabilitados}
        onLogout={handleLogout}
        logoConfig={logoConfig}
        logoScale={logoScale}
        onLogoUpdated={cargarBrandingOptica}
      />

      {/* Banner superior de demostración */}
      {usuario && isDemoUser(usuario) && (
        <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-cyan-950 text-white px-4 py-2.5 text-xs flex items-center justify-between shadow-md border-b border-indigo-500/20 fixed top-16 left-0 right-0 z-[950]">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse shrink-0" />
            <span className="font-extrabold text-cyan-300 uppercase tracking-wider text-[11px] shrink-0">Modo Demostración</span>
            <span className="text-slate-300 hidden md:inline truncate">
              Estás explorando con datos de prueba. Para cargar tu propia información y pacientes, activa tu óptica.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <button
              type="button"
              onClick={abrirTourDemo}
              className="px-2.5 py-1 font-bold text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
            >
              Ver Tour
            </button>
            <button
              type="button"
              onClick={() => solicitarRegistroDemo('activar tu propia óptica con 3 meses gratis')}
              className="px-3 py-1 font-black text-white bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-500 hover:to-indigo-500 rounded-lg shadow-sm transition-all cursor-pointer"
            >
              <span className="hidden sm:inline">Activar 3 Meses Gratis</span>
              <span className="sm:hidden">Activar Óptica</span>
            </button>
          </div>
        </div>
      )}

      <div className={`px-4 sm:px-6 pb-6 ${usuario && isDemoUser(usuario) ? 'pt-32' : 'pt-20'}`}>
        {/* Botón de Back debajo del logo del navbar (solo mobile y dispositivos medianos) */}
        <div className="max-w-[1700px] w-full mx-auto mb-3 flex items-center lg:hidden">
          <button
            type="button"
            onClick={handleGoBack}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-200 rounded-xl shadow-2xs hover:shadow-xs transition-all cursor-pointer group"
            title={vistaAnterior ? `Volver a ${obtenerNombreVista(vistaAnterior)}` : 'Volver atrás'}
          >
            <ArrowLeftIcon className="w-4 h-4 text-slate-600 group-hover:text-blue-600 group-hover:-translate-x-0.5 transition-all" />
            <span>Volver</span>
            {vistaAnterior && (
              <span className="text-slate-400 font-normal hidden sm:inline">
                · {obtenerNombreVista(vistaAnterior)}
              </span>
            )}
          </button>
        </div>

        {viewComponent}
      </div>

      {/* Floating speed-dial: Nueva Venta / Nuevo Cliente (oculto cuando hay modales abiertos) */}
      {!modalGlobalAbierto && puedeAccederVista('nuevo-cliente', modulosHabilitados, esSuperAdmin) && (
        <FloatingAddButton
          onNuevoCliente={() => {
            if (isDemoUser(usuario)) {
              solicitarRegistroDemo('registrar nuevos pacientes y recetas');
              return;
            }
            handleViewChange('nuevo-cliente');
          }}
          onNuevaVenta={() => {
            if (isDemoUser(usuario)) {
              solicitarRegistroDemo('crear y registrar ventas');
              return;
            }
            setMostrarNuevaVenta(true);
          }}
        />
      )}

      {/* Modal quick-add Venta */}
      {mostrarNuevaVenta && (
        <VentaModal
          onClose={() => setMostrarNuevaVenta(false)}
          onSuccess={() => {}}
        />
      )}

      {/* Modal Tour de Demostración */}
      <ModalDemoTour
        isOpen={mostrarTourDemo}
        onClose={() => {
          setMostrarTourDemo(false);
          sessionStorage.setItem('demo_tour_visto', 'true');
        }}
        onAbrirRegistro={() => {
          setMotivoRegistroTrial('activar tu propia óptica con 3 meses gratis');
          setMostrarRegistroTrial(true);
        }}
      />

      {/* Modal Registro de Nueva Óptica Trial */}
      <ModalRegistroOpticaDemo
        isOpen={mostrarRegistroTrial}
        onClose={() => setMostrarRegistroTrial(false)}
        onRegistroExitoso={handleRegistroTrialExitoso}
        motivo={motivoRegistroTrial}
      />
    </div>
  );
}

export default App;
